/**
 * Dynacare Walk-In Confirmation Modal
 * Compatible with Dawn + Skio subscription app (MasterForm / MasterFormChild).
 *
 * Event strategy (three layers, because Skio can swallow standard events):
 *   1. document-level 'change'      — catches direct radio/select interactions
 *   2. 'variant:change' custom event — Dawn dispatches this on <product-info>
 *                                      after EVERY variant switch; Skio-safe
 *   3. MutationObserver on form area — catches Skio's async DOM re-renders
 *      and re-evaluates selected draw option when the product form is rebuilt
 */

(function () {
  'use strict';

  var DYNACARE_VALUE = 'Dynacare Lab Walk-In';
  var AT_HOME_VALUE  = 'At-Home Blood Draw';
  var confirmedProducts = {};
  var modal = null;

  // ─── Modal HTML (self-inject fallback if Liquid snippet is absent) ─────────

  var MODAL_HTML = '<div id="dynacare-modal" class="dynacare-modal" role="dialog" aria-modal="true" aria-labelledby="dynacare-modal-title" hidden>'
    + '<div class="dynacare-modal__overlay"></div>'
    + '<div class="dynacare-modal__container">'
    +   '<div class="dynacare-modal__content">'
    +     '<div class="dynacare-modal__eyebrow">Walk-In Pricing Confirmed</div>'
    +     '<h2 class="dynacare-modal__title" id="dynacare-modal-title">You\'ve unlocked Dynacare Walk-In pricing</h2>'
    +     '<p class="dynacare-modal__body">Nice choice \u2014 instead of an at-home visit, you\'ll pop into any Dynacare location in <strong>Ontario, Quebec, or Manitoba</strong> at a time that suits you. No appointment needed, just walk in. Everything else is identical: same tests, same coaching session, same results in your app.</p>'
    +     '<div class="dynacare-modal__actions">'
    +       '<button id="dynacare-confirm-btn" class="dynacare-modal__btn dynacare-modal__btn--primary" type="button">Confirmed \u2014 I\'ll visit a Dynacare location \u2192</button>'
    +       '<button id="dynacare-decline-btn" class="dynacare-modal__btn dynacare-modal__btn--secondary" type="button">Switch back to at-home blood draw</button>'
    +     '</div>'
    +     '<p class="dynacare-modal__disclaimer">Available in Ontario, Quebec, and Manitoba only.</p>'
    +   '</div>'
    + '</div>'
    + '</div>';

  function injectModal() {
    if (document.getElementById('dynacare-modal')) return; // Liquid already rendered it
    document.body.insertAdjacentHTML('beforeend', MODAL_HTML);
    console.log('[Dynacare] Modal self-injected (Liquid snippet not found — add {% render \'dynacare-modal\' %} to main-product.liquid for best results).');
  }

  // ─── Initialize ────────────────────────────────────────────────────────────

  function init() {
    injectModal();

    modal = document.getElementById('dynacare-modal');
    if (!modal) {
      console.error('[Dynacare] Modal not in DOM after injection. Aborting.');
      return;
    }

    // Bind modal buttons
    var confirmBtn = document.getElementById('dynacare-confirm-btn');
    var declineBtn = document.getElementById('dynacare-decline-btn');
    var overlay    = modal.querySelector('.dynacare-modal__overlay');
    if (confirmBtn) confirmBtn.addEventListener('click', onConfirm);
    if (declineBtn) declineBtn.addEventListener('click', onDecline);
    if (overlay)    overlay.addEventListener('click', onDecline);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hasAttribute('hidden')) onDecline();
    });

    // ── Layer 1: Standard change events ──────────────────────────────────────
    // Works for most cases. May be swallowed by Skio in some setups.
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!isDrawOptionInput(t)) return;
      if (t.type === 'radio' && !t.checked) return;
      var form = findAssociatedForm(t);
      if (!form) { console.warn('[Dynacare] No form found for input', t); return; }
      handleDrawChange(t.value, form);
    });

    // ── Layer 2: Dawn/Skio variant:change custom event ────────────────────────
    // Dawn's product-info.js dispatches this on the <product-info> element after
    // every variant switch. Skio maintains compatibility with this event.
    // This is the most reliable hook when Skio intercepts standard change events.
    document.addEventListener('variant:change', function (e) {
      // e.target is the <product-info> element; e.detail.variant is the new variant
      var productInfoEl = e.target.closest
        ? (e.target.tagName === 'PRODUCT-INFO' ? e.target : e.target.closest('product-info'))
        : null;
      if (!productInfoEl) productInfoEl = e.target;

      // Find which draw option is now checked inside this product-info
      var drawInput = productInfoEl.querySelector(
        'input[type="radio"][value="' + DYNACARE_VALUE + '"]:checked, ' +
        'input[type="radio"][value="' + AT_HOME_VALUE + '"]:checked'
      );
      if (!drawInput) {
        // Also check select elements
        var sel = findDrawSelect(productInfoEl);
        if (sel) drawInput = { value: sel.value, tagName: 'SELECT', _form: findAssociatedForm(sel) };
      }
      if (!drawInput) return;

      var form = drawInput._form || findAssociatedForm(drawInput);
      if (!form) return;
      handleDrawChange(drawInput.value, form);
    });

    // ── Layer 3: MutationObserver — Skio async re-render guard ────────────────
    // Skio rebuilds the product form subtree after fetching subscription data.
    // We watch product-info / skio-master-form containers for child mutations.
    // When a rebuild is detected we re-evaluate the current draw selection.
    setupSkioObservers();

    // Evaluate on load (e.g. page loads with Dynacare pre-selected)
    setTimeout(checkInitialState, 200);

    console.log('[Dynacare] Initialized. Listening on three event layers.');
  }

  // ─── Skio MutationObserver setup ──────────────────────────────────────────

  function setupSkioObservers() {
    var containers = Array.from(document.querySelectorAll(
      'product-info, skio-master-form, skio-master-form-child'
    ));

    if (containers.length === 0) {
      // Skio elements may not be in DOM yet — retry after a short delay
      setTimeout(setupSkioObservers, 800);
      return;
    }

    containers.forEach(function (el) {
      if (el._dynacareObserving) return;
      el._dynacareObserving = true;

      var observer = new MutationObserver(function () {
        // Skio re-rendered — re-evaluate draw selection for this container
        evaluateDrawInContainer(el);
      });
      observer.observe(el, { childList: true, subtree: true });
    });
  }

  function evaluateDrawInContainer(container) {
    var dynacareInput = container.querySelector(
      'input[type="radio"][value="' + DYNACARE_VALUE + '"]:checked'
    );
    var atHomeInput = container.querySelector(
      'input[type="radio"][value="' + AT_HOME_VALUE + '"]:checked'
    );

    if (dynacareInput) {
      var form = findAssociatedForm(dynacareInput);
      if (form) handleDrawChange(DYNACARE_VALUE, form);
    } else if (atHomeInput) {
      var form = findAssociatedForm(atHomeInput);
      if (form) handleDrawChange(AT_HOME_VALUE, form);
    } else {
      // Check selects
      var sel = findDrawSelect(container);
      if (sel) {
        var form = findAssociatedForm(sel);
        if (form) handleDrawChange(sel.value, form);
      }
    }
  }

  // ─── Draw Option Detection ─────────────────────────────────────────────────

  function isDrawOptionInput(input) {
    if (!input || !input.tagName) return false;
    if (input.tagName === 'SELECT') {
      return !!findDrawSelect(input.parentElement || document, input);
    }
    if (input.tagName === 'INPUT' && input.type === 'radio') {
      return input.value === DYNACARE_VALUE || input.value === AT_HOME_VALUE;
    }
    return false;
  }

  function findDrawSelect(scope, targetSelect) {
    var selects = (targetSelect ? [targetSelect] : Array.from((scope || document).querySelectorAll('select')));
    for (var i = 0; i < selects.length; i++) {
      var opts = Array.from(selects[i].options || []).map(function (o) { return o.value; });
      if (opts.indexOf(DYNACARE_VALUE) !== -1 || opts.indexOf(AT_HOME_VALUE) !== -1) {
        return selects[i];
      }
    }
    return null;
  }

  // ─── Find Associated Form ──────────────────────────────────────────────────
  // Priority: Skio wrapper → product-info → data-product-id → single form → closest

  function findAssociatedForm(input) {
    if (!input || !input.closest) return null;

    // 1. Skio wrapper (skio-master-form contains the real submit button)
    var skioMaster = input.closest('skio-master-form');
    if (skioMaster) {
      var f = skioMaster.querySelector('form[action*="/cart/add"], form[action*="cart"]');
      if (f) return f;
      // Skio sometimes uses a form without /cart/add if it handles submission itself
      var f2 = skioMaster.querySelector('form');
      if (f2) return f2;
    }

    // 2. Dawn product-info
    var productInfo = input.closest('product-info');
    if (productInfo) {
      var f = productInfo.querySelector('form[action*="/cart/add"]');
      if (f) return f;
    }

    // 3. Any ancestor with data-product-id
    var productEl = input.closest('[data-product-id]');
    if (productEl) {
      var f = productEl.querySelector('form[action*="/cart/add"]');
      if (f) return f;
    }

    // 4. Only one cart form on page
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    if (forms.length === 1) return forms[0];

    // 5. Direct form ancestor
    return input.closest('form') || null;
  }

  // ─── Handle Draw Change ────────────────────────────────────────────────────

  function handleDrawChange(selectedValue, form) {
    var productId = getProductId(form);
    console.log('[Dynacare] Draw changed to "' + selectedValue + '" | product:', productId);

    if (selectedValue === DYNACARE_VALUE) {
      if (!confirmedProducts[productId]) {
        disableAtcButton(form);
        openModal(form);
      }
      // If already confirmed this session, leave ATC enabled
    } else {
      // Switched away from Dynacare — clear confirmation state
      delete confirmedProducts[productId];
      enableAtcButton(form);
      if (!modal.hasAttribute('hidden')) closeModal();
    }
  }

  function checkInitialState() {
    var dynacareInputs = document.querySelectorAll(
      'input[type="radio"][value="' + DYNACARE_VALUE + '"]:checked'
    );
    dynacareInputs.forEach(function (input) {
      var form = findAssociatedForm(input);
      if (!form) return;
      var productId = getProductId(form);
      if (!confirmedProducts[productId]) disableAtcButton(form);
    });

    // Check selects too
    var drawSelect = findDrawSelect(document);
    if (drawSelect && drawSelect.value === DYNACARE_VALUE) {
      var form = findAssociatedForm(drawSelect);
      if (form) {
        var productId = getProductId(form);
        if (!confirmedProducts[productId]) disableAtcButton(form);
      }
    }
  }

  // ─── ATC Button ───────────────────────────────────────────────────────────
  // Searches in order: form scope → skio-master-form → product-form → page fallback

  function getAtcButton(form) {
    var candidates = [
      form,
      document.querySelector('skio-master-form'),
      document.querySelector('skio-master-form-child'),
      document.querySelector('product-form'),
      document.querySelector('[data-product-id]'),
    ];

    for (var i = 0; i < candidates.length; i++) {
      var scope = candidates[i];
      if (!scope || !scope.querySelector) continue;
      var btn = scope.querySelector('[name="add"]')
             || scope.querySelector('button[type="submit"].product-form__submit')
             || scope.querySelector('button[type="submit"]');
      if (btn) return btn;
    }
    return null;
  }

  function disableAtcButton(form) {
    var btn = getAtcButton(form);
    if (!btn) { console.warn('[Dynacare] ATC button not found — cannot block.'); return; }
    btn.setAttribute('disabled', 'disabled');
    btn.setAttribute('data-dynacare-blocked', 'true');
    console.log('[Dynacare] ATC button disabled.');
  }

  function enableAtcButton(form) {
    // Enable all blocked buttons (covers membership page with multiple panels)
    var blocked = document.querySelectorAll('[data-dynacare-blocked="true"]');
    blocked.forEach(function (btn) {
      btn.removeAttribute('disabled');
      btn.removeAttribute('data-dynacare-blocked');
    });
    if (blocked.length) console.log('[Dynacare] ATC button(s) enabled.');
  }

  // ─── Product ID ────────────────────────────────────────────────────────────

  function getProductId(form) {
    if (!form) return 'unknown';
    if (form.querySelector) {
      var idInput = form.querySelector('[name="id"]');
      if (idInput && idInput.value) return idInput.value;
    }
    // Try parent context
    var productEl = form.closest && form.closest('[data-product-id]');
    if (productEl) return productEl.getAttribute('data-product-id');
    // Fallback: index among cart forms
    var forms = Array.from(document.querySelectorAll('form[action*="/cart/add"], form'));
    var idx = forms.indexOf(form);
    return 'form-' + (idx >= 0 ? idx : 'x');
  }

  // ─── Modal open / close ────────────────────────────────────────────────────

  function openModal(form) {
    modal._activeForm = form;
    modal.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    var btn = document.getElementById('dynacare-confirm-btn');
    if (btn) setTimeout(function () { btn.focus(); }, 60);
    console.log('[Dynacare] Modal opened.');
  }

  function closeModal() {
    modal.setAttribute('hidden', '');
    modal._activeForm = null;
    document.body.style.overflow = '';
  }

  function onConfirm() {
    var form = modal._activeForm;
    if (form) {
      confirmedProducts[getProductId(form)] = true;
      enableAtcButton(form);
    }
    closeModal();
    console.log('[Dynacare] Confirmed. ATC unlocked.');
  }

  function onDecline() {
    var form = modal._activeForm;
    closeModal();
    if (!form) return;

    // Search scope: prefer the tightest container Skio manages
    var scope = (form.closest && (
      form.closest('skio-master-form') ||
      form.closest('product-info') ||
      form.closest('[data-product-id]')
    )) || document;

    // Reset radio
    var atHomeRadio = scope.querySelector(
      'input[type="radio"][value="' + AT_HOME_VALUE + '"]'
    );
    if (atHomeRadio) {
      atHomeRadio.checked = true;
      // Fire 'input' then 'change' — Skio listens to both
      atHomeRadio.dispatchEvent(new Event('input',  { bubbles: true }));
      atHomeRadio.dispatchEvent(new Event('change', { bubbles: true }));
      enableAtcButton(form);
      return;
    }

    // Reset select
    var sel = findDrawSelect(scope);
    if (sel) {
      sel.value = AT_HOME_VALUE;
      sel.dispatchEvent(new Event('change', { bubbles: true }));
      enableAtcButton(form);
    }
  }

  // ─── Boot ──────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();